import type { ItemLite, ProductCreate, SpaceCreate } from "@plannello/shared";
import { api } from "@/lib/api";
import { queryClient } from "@/query-client";
import { rememberKeyChange, spacesQuery } from "./catalog";
import type { Product, ProductSchema, Space } from "./types";

/** Creates a space (and its space-wide product); the sidebar tree refreshes from the server. */
export async function createSpace(input: SpaceCreate): Promise<{ space: Space; product: Product }> {
  const res = await api.post<{ space: Space; product: Product }>("/api/v1/spaces", input);
  await queryClient.invalidateQueries({ queryKey: spacesQuery.queryKey });
  return res;
}

export async function createProduct(input: ProductCreate): Promise<Product> {
  const res = await api.post<Product>("/api/v1/products", input);
  await queryClient.invalidateQueries({ queryKey: spacesQuery.queryKey });
  return res;
}

/** What deleting a space or product would take along (GET /spaces|products/:key/impact). */
export type DeletionImpact = {
  items: number;
  trashed: number;
  comments: number;
  products: { key: string; name: string; isSpaceProduct: boolean }[];
};

/** Result of a delete: how many items and files went with it. */
export type DeleteResult = {
  id: string;
  key: string;
  deleted: true;
  products: string[];
  purged: { items: number; comments: number; relations: number; attachments: number; files: number };
};

type Target = { kind: "space" | "product"; key: string };
const base = (t: Target) => `/api/v1/${t.kind === "space" ? "spaces" : "products"}/${t.key}`;

/** Archive or unarchive: read-only and hidden from the sidebar, lists and search while archived. */
export async function setArchived(t: Target, archived: boolean): Promise<void> {
  await api.post(`${base(t)}/${archived ? "archive" : "unarchive"}`);
  await refreshAfterLifecycle();
}

export const fetchImpact = (t: Target) => api.get<DeletionImpact>(`${base(t)}/impact`);

/** Delete an empty space or product, or (`purge`) with every item, comment and relation in it. */
export async function deleteTarget(t: Target, purge: boolean): Promise<DeleteResult> {
  const res = await api.delete<DeleteResult>(`${base(t)}${purge ? "?purge=1" : ""}`);
  await refreshAfterLifecycle();
  return res;
}

/** Spaces, schemas and every cached item list may have changed (the live stream tells other tabs and people). */
async function refreshAfterLifecycle() {
  await queryClient.invalidateQueries({ queryKey: spacesQuery.queryKey });
  void queryClient.invalidateQueries({ queryKey: ["lites"] });
  void queryClient.invalidateQueries({ queryKey: ["search"] });
  void queryClient.invalidateQueries({ queryKey: ["me", "permissions"] });
}

/**
 * Suggests a key from a name: initials of up to three words, else the first letters, uppercase
 * ASCII only ("Northwind Storefront" -> "NS", "Docs" -> "DOCS"). Umlauts fold to their base letter.
 */
export function suggestKey(name: string): string {
  const ascii = name
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, " ")
    .trim();
  const words = ascii.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  let key =
    words.length === 1
      ? words[0]!.slice(0, 4)
      : words
          .slice(0, 4)
          .map((w) => w[0])
          .join("");
  key = key.replace(/^[0-9]+/, "");
  if (key.length === 1) key = `${words[0]!.replace(/^[0-9]+/, "")}X`.slice(0, 3);
  return key.slice(0, 10);
}

export const KEY_RE = /^[A-Z][A-Z0-9]{1,9}$/;

// ---------------------------------------------------------------------------------------------------------------
// Inline renaming: name, description and key of a product or space (PATCH /products|spaces/:key).
// ---------------------------------------------------------------------------------------------------------------

export type RenameTarget = { kind: "space" | "product"; key: string };
export type RenamePatch = { name?: string; description?: string | null; key?: string };

/** Replaces the product part of a key: ("PLZ", "SHOP", "PLZ-12") gives "SHOP-12". */
const rekey = (key: string, from: string, to: string) =>
  key.startsWith(`${from}-`) ? `${to}${key.slice(from.length)}` : key;

/**
 * Applies a key change to every cache that knows keys, so the new URL resolves at once and nothing flashes
 * "not found" while refetches run: the sidebar tree, the schema queries (keyed by product key), the lite rows of the
 * renamed products (the server bumped their versions by one, so its item.updated events are recognised as already
 * applied) and, by refetch, open item details, search results and feeds.
 */
function applyKeyChange(t: RenameTarget, row: { key: string }) {
  const from = t.key.toUpperCase();
  const to = row.key;
  if (from === to) return;
  const qc = queryClient;
  const renamed: string[] = [];
  // Before the tree changes: the page still showing the old key must keep resolving it (see rememberKeyChange).
  rememberKeyChange(t.kind, from, to);
  if (t.kind === "space") rememberKeyChange("product", from, to);
  qc.setQueryData<Space[]>(spacesQuery.queryKey, (spaces) =>
    spaces?.map((s) => {
      const ownsKey = t.kind === "space" ? s.key === from : s.products.some((p) => p.key === from);
      if (!ownsKey) return s;
      return {
        ...s,
        key: t.kind === "space" ? to : s.key,
        products: s.products.map((p) => {
          const hit = t.kind === "space" ? p.isSpaceProduct : p.key === from;
          if (!hit) return p;
          renamed.push(p.id);
          return { ...p, key: to };
        }),
      };
    }),
  );
  const schema = qc.getQueryData<ProductSchema>(["schema", from]);
  if (schema) {
    qc.setQueryData(["schema", to], { ...schema, product: { ...schema.product, key: to } });
    qc.removeQueries({ queryKey: ["schema", from] });
  }
  // A space rename changes the space shown in every schema of its products.
  void qc.invalidateQueries({ queryKey: ["schema"] });
  for (const id of renamed) {
    qc.setQueryData<ItemLite[]>(["lites", id], (list) =>
      list?.map((i) => ({ ...i, key: rekey(i.key, from, to), version: i.version + 1 })),
    );
  }
  for (const key of ["item", "search", "activity", "backlinks", "trash"]) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

/** Sidebar rows carry name and description: merge a server row into the cached tree. */
function applyRowToTree(t: RenameTarget, row: { key: string; name: string; description: string | null }) {
  queryClient.setQueryData<Space[]>(spacesQuery.queryKey, (spaces) =>
    spaces?.map((s) => {
      if (t.kind === "space" && s.key === row.key) {
        return {
          ...s,
          name: row.name,
          description: row.description,
          products: s.products.map((p) =>
            p.isSpaceProduct ? { ...p, name: row.name, description: row.description } : p,
          ),
        };
      }
      if (t.kind === "product" && s.products.some((p) => p.key === row.key)) {
        return {
          ...s,
          products: s.products.map((p) =>
            p.key === row.key ? { ...p, name: row.name, description: row.description } : p,
          ),
        };
      }
      return s;
    }),
  );
}

/**
 * Saves name, description and/or key. The caches follow the server's answer; a key change also rewrites the
 * cached item keys (see applyKeyChange). Throws the ApiError (422 reasons `key_taken`, `invalid_key`,
 * `space_product`, `product_archived`) so the field can say what is wrong.
 */
export async function patchTarget(
  t: RenameTarget,
  patch: RenamePatch,
): Promise<{ key: string; name: string; description: string | null }> {
  const row = await api.patch<{ key: string; name: string; description: string | null }>(base(t), patch);
  if (patch.key !== undefined) applyKeyChange(t, row);
  applyRowToTree(t, row);
  return row;
}
